import { Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { CoreButton, coreToast } from "@verity/components/core";
import { useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import apiClient from "../../../../../../helpers/api-client";
import i18n from "../../../../../../i18n";
import { PortalControllerContext } from "../../../../../context";
import { ConferencePlatformType, InterviewDetailsType } from "../../../ScheduleInterview/types";
import { getPlatformLabel } from "../../../utils";
import { CameraIcon, CheckIcon, ClockIcon, DeleteIcon, GroupIcon } from "../assets";
import { InterviewType, Status } from "../types";
import DeleteInterviewModal from "./DeleteInterviewModal";
import styles from "./InterviewCard.module.css";

function formatDuration(start: Date, end: Date): string {
	const diffMs = Math.abs(end.getTime() - start.getTime());

	const totalMinutes = Math.ceil(diffMs / (1000 * 60));

	const days = Math.floor(totalMinutes / (60 * 24));
	const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
	const minutes = totalMinutes % 60;

	const parts: string[] = [];

	if (days > 0) {
		parts.push(`${days} day${days !== 1 ? "s" : ""}`);
	}

	if (hours > 0) {
		parts.push(`${hours} hr${hours !== 1 ? "s" : ""}`);
	}

	// Always show minutes
	parts.push(`${minutes} min${minutes !== 1 ? "s" : ""}`);

	return parts.join(" ");
}

function DetailItem({ icon, text }: { icon: React.ReactNode; text: string }) {
	return (
		<Stack direction="row" spacing="8px" alignItems="center">
			{icon}
			<Typography className={styles.details}>{text}</Typography>
		</Stack>
	);
}

type InterviewCardProps = {
	interview: InterviewType;
};

export default function InterviewCard({ interview }: InterviewCardProps) {
	const { userInfo } = useContext(PortalControllerContext);

	const navigate = useNavigate();
	const location = useLocation();
	const queryClient = useQueryClient();
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [endLoading, setEndLoading] = useState(false);

	const {
		id,
		quizId,
		proctoringOptions: { candidateInterviewOptions },
		liveSession: session,
		status,
	} = interview;
	const currentUserUuid = userInfo?.uuid;

	const { candidateName, positionName, title, videoPlatform } = candidateInterviewOptions;
	const { startTime, endTime, users } = session;

	const interviewersCount = users.filter((u) => u.role === "proctor").length;
	const start = new Date(startTime);
	const end = new Date(endTime);

	const statusDetails = {
		[Status.Active]: {
			label: "Active",
			color: "rgb(221, 231, 225)",
			time: i18n.t("candidate.instructorDashboard.interviewCard.time.active", {
				time: formatDuration(start, new Date()),
			}),
			buttonText: i18n.t("candidate.instructorDashboard.interviewCard.buttons.join"),
			buttonAction: () => navigate(`/interviews/${id}?quizId=${quizId}`),
		},
		[Status.ActiveUpcoming]: {
			label: "Active",
			color: "rgb(221, 231, 225)",
			time: i18n.t("candidate.instructorDashboard.interviewCard.time.upcoming", {
				time: formatDuration(new Date(), start),
			}),
			buttonText: i18n.t("candidate.instructorDashboard.interviewCard.buttons.join"),
			buttonAction: () => navigate(`/interviews/${id}?quizId=${quizId}`),
		},
		[Status.Completed]: {
			label: "Completed",
			color: "rgb(230, 230, 230)",
			time: i18n.t("candidate.instructorDashboard.interviewCard.time.completed", {
				time: formatDuration(end, new Date()),
			}),
			buttonText: i18n.t("candidate.instructorDashboard.interviewCard.buttons.results"),
			buttonAction: () => navigate(`/results/${id}`, { state: { from: location.pathname + location.search } }),
		},
		[Status.Upcoming]: {
			label: "Upcoming",
			color: "rgb(213, 221, 240)",
			time: i18n.t("candidate.instructorDashboard.interviewCard.time.upcoming", {
				time: formatDuration(new Date(), start),
			}),
			buttonText: i18n.t("candidate.instructorDashboard.interviewCard.buttons.edit"),
			buttonAction: async () =>
				navigate("/interviews/schedule", {
					state: {
						assessmentId: id,
						assessmentUuid: quizId,
						...(await getScheduleData()),
					},
				}),
		},
	};

	async function getScheduleData() {
		const { title, candidateName, candidateEmail, positionName, videoPlatform, meetingLink, notes } =
			candidateInterviewOptions;
		const { startTime, endTime, users } = session;

		const interviewDetails: InterviewDetailsType = {
			title,
			candidateName,
			candidateEmail,
			positionName,
			interviewerUuids: users.filter((u) => u.role === "proctor").map((u) => u.userId),
			startTime: new Date(startTime),
			endTime: new Date(endTime),
		};
		const conferencePlatform: ConferencePlatformType = {
			videoPlatform,
			meetingLink,
			notes: notes || "",
		};

		const proctoringOption = await apiClient.proctoringConfiguration.get({
			quizId: quizId,
			courseId: session.instituteId!, //access control institute id = lms course id?
		});

		return {
			interviewDetails,
			conferencePlatform,
			proctoringOption: proctoringOption?.proctoringOptionProfile.proctoringOption,
		};
	}

	async function handleEndSession() {
		setEndLoading(true);
		try {
			const success = await apiClient.liveClient.endLiveSession(session.id);
			if (success) {
				coreToast.success(i18n.t("candidate.instructorDashboard.interviewCard.toast.completeSuccess"));
				void queryClient.invalidateQueries({ queryKey: ["institute-interviews"] });
			} else {
				coreToast.error(i18n.t("candidate.instructorDashboard.interviewCard.toast.completeFail"));
			}
		} catch (error) {
			coreToast.error(i18n.t("candidate.instructorDashboard.interviewCard.toast.completeFail"));
		} finally {
			setEndLoading(false);
		}
	}

	const disableJoinButton =
		(status === Status.Active || status === Status.ActiveUpcoming) &&
		interview.liveSession.users.every((user) => user.userId !== currentUserUuid);

	return (
		<div className={styles.card}>
			<Stack direction="row" width="100%" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap="5px">
				<Stack direction="row" spacing="16px" alignItems="center">
					{/* <CoreProfileImage name={data.candidateName} size={48} /> */}
					<Stack spacing="5px">
						<Stack direction="row" columnGap="12px" alignItems="center" flexWrap="wrap">
							<Typography className={styles.name}>{candidateName}</Typography>
							<Chip label={statusDetails[status].label} sx={{ backgroundColor: statusDetails[status].color }} />
							<Typography className={styles.details}>
								{positionName} • {title}
							</Typography>
						</Stack>
						<Stack direction="row" columnGap="24px" alignItems="center" flexWrap="wrap">
							<DetailItem icon={<ClockIcon />} text={statusDetails[status].time} />
							<DetailItem
								icon={<GroupIcon />}
								text={i18n.t("candidate.instructorDashboard.interviewCard.panel", { count: interviewersCount })}
							/>
							<DetailItem
								icon={<CameraIcon />}
								text={i18n.t("candidate.instructorDashboard.interviewCard.platform", {
									platform: getPlatformLabel(videoPlatform),
								})}
							/>
						</Stack>
					</Stack>
				</Stack>
				<Stack direction="row" gap="8px">
					<Tooltip
						disableHoverListener={!disableJoinButton}
						title={i18n.t("candidate.instructorDashboard.interviewCard.tooltip.disabled")}
					>
						<span>
							<CoreButton
								style={{ flexShrink: 0 }}
								disabled={disableJoinButton}
								onClick={statusDetails[status].buttonAction}
							>
								{statusDetails[status].buttonText}
							</CoreButton>
						</span>
					</Tooltip>
					{(status === Status.Upcoming || status === Status.ActiveUpcoming) && (
						<Tooltip title={i18n.t("candidate.instructorDashboard.interviewCard.tooltip.cancel")}>
							<IconButton className={styles.delete} onClick={() => setDeleteModalOpen(true)}>
								<DeleteIcon color="red" />
							</IconButton>
						</Tooltip>
					)}
					{status === Status.Active && (
						<Tooltip title={i18n.t("candidate.instructorDashboard.interviewCard.tooltip.complete")}>
							<IconButton className={styles.complete} onClick={handleEndSession} disabled={endLoading}>
								<CheckIcon />
							</IconButton>
						</Tooltip>
					)}
				</Stack>
			</Stack>
			{deleteModalOpen && (
				<DeleteInterviewModal
					open={deleteModalOpen}
					setOpen={setDeleteModalOpen}
					assessmentId={id}
					candidateName={candidateName}
					startTime={new Date(startTime)}
				/>
			)}
		</div>
	);
}
